/* =========================================================================
   Place des Arts — bascule d'unités cm ↔ pouces.
   Rendu du choix : snippets/pda-geo-selector.liquid (3ᵉ champ du sélecteur géo).
   -------------------------------------------------------------------------
   ⛔ CE MODULE NE TOUCHE JAMAIS AU MOTEUR DE FILTRES, ET C'EST TOUT LE SUJET.
   Les curseurs Largeur/Hauteur envoient des bornes que `pda-index-core.js` compare
   à un index stocké EN CENTIMÈTRES, servi en fichier statique à tous les visiteurs.
   La conversion vit donc À LA FRONTIÈRE DU RENDU : ce qui est AFFICHÉ change, ce qui
   est COMPARÉ jamais. Convertir un cran plus bas ferait filtrer des pouces contre des
   centimètres — la grille rendrait des résultats, simplement les mauvais, sans rien
   signaler ni à l'écran ni en console. C'est le défaut que ce fichier existe pour
   rendre impossible.

   ⛔ ON RECALCULE DEPUIS LA VALEUR BRUTE, JAMAIS DEPUIS LE TEXTE PEINT. Le Liquid
   arrondit avant d'afficher (`| round`) : relire « 56 cm » et convertir donnerait
   22,05 in, puis un retour en cm donnerait 56 — mais un aller-retour de plus dériverait.
   Chaque élément convertible porte donc ses valeurs d'origine dans `data-pda-dim-cm`.
   ⚠️ `-cm` DANS LE NOM, ET CE N'EST PAS DÉCORATIF : `data-pda-dim` était DÉJÀ pris par
   les groupes de facettes (`data-pda-dim="w"` / `"hg"`, snippets/pda-facet-control).
   Mesuré en le heurtant : le sélecteur du module attrapait les groupes de filtres avant
   les cards. Le suffixe dit ce que l'attribut PORTE — des centimètres — pas ce qu'il est.
   Un élément SANS cet attribut n'est pas converti : mieux vaut une surface oubliée,
   visible, qu'une valeur fausse, invisible.

   ⛔ LE RE-RENDU EST LE PIÈGE PRINCIPAL. Les grilles du catalogue, de /search et des
   favoris sont réinjectées à chaud par la Section Rendering API : les cards reviennent
   du serveur EN CENTIMÈTRES. Sans observateur, le choix « pouces » tiendrait jusqu'au
   premier filtre, puis se perdrait carte par carte, silencieusement. D'où le
   MutationObserver — il n'est pas un raffinement, il est la condition du bon
   fonctionnement.
   ========================================================================= */

(() => {
  const CLE = 'pda:unit';
  const CM_PAR_POUCE = 2.54;

  /* ⚠️ `localStorage` peut JETER (Safari en navigation privée historiquement, et tout
   * navigateur avec les cookies tiers coupés dans un iframe). Un throw ici tuerait le
   * module au chargement, donc la conversion ET le libellé du sélecteur. On dégrade
   * vers la mémoire : le choix ne survit pas à la navigation, mais la page fonctionne. */
  let memoire = null;
  const lire = () => {
    try { return localStorage.getItem(CLE); } catch { return memoire; }
  };
  const ecrire = (v) => {
    memoire = v;
    try { localStorage.setItem(CLE, v); } catch { /* mémoire seule */ }
  };

  const unite = () => (lire() === 'in' ? 'in' : 'cm');

  const trad = (cle, params) =>
    (window.PDA && typeof window.PDA.t === 'function') ? window.PDA.t(cle, params) : cle;

  /** Formate un nombre selon la locale active, sans décimale inutile. */
  const nombre = (n) => {
    const arrondi = Math.round(n * 10) / 10;
    const entier = Number.isInteger(arrondi) ? arrondi : arrondi;
    return (window.PDA && PDA.nombre) ? PDA.nombre(entier) : String(entier);
  };

  /* ⛔ LES DEUX FORMATS DU THÈME SONT RÉELS, PAS UNE COQUILLE. Les cards et la fiche
   * œuvre écrivent « 56 x 38 cm » (espaces autour du x, espace avant l'unité) ; le
   * cartel du panier écrit « 81x57cm », collé. Mesuré le 20/08. Les aplatir serait un
   * changement de rendu sur une surface qu'on ne vient pas corriger — l'élément porte
   * donc son format, et on le respecte. */
  function assembler(valeurs, format, symbole) {
    const sep = format === 'tight' ? 'x' : ' x ';
    const avant = format === 'tight' ? '' : ' ';
    return valeurs.join(sep) + avant + symbole;
  }

  /** Convertit UN élément porteur de `data-pda-dim-cm`. Idempotent. */
  function convertirElement(el, u) {
    const brut = el.getAttribute('data-pda-dim-cm');
    if (!brut) return;
    const format = el.getAttribute('data-pda-dim-cm-fmt') || 'spaced';
    const valeurs = brut.split(',').map((x) => parseFloat(x.trim())).filter((n) => !Number.isNaN(n));
    if (!valeurs.length) return;
    const symbole = trad(u === 'in' ? 'pda.geo.symbole_in' : 'pda.geo.symbole_cm');
    const converties = u === 'in'
      ? valeurs.map((v) => nombre(v / CM_PAR_POUCE))
      : valeurs.map((v) => nombre(v));
    const texte = assembler(converties, format, symbole);
    if (el.textContent !== texte) el.textContent = texte;
  }

  /** Convertit les SUFFIXES d'unité isolés (champs Min/Max des filtres, libellés). */
  function convertirSymboles(u, racine) {
    const symbole = trad(u === 'in' ? 'pda.geo.symbole_in' : 'pda.geo.symbole_cm');
    for (const el of racine.querySelectorAll('[data-pda-unit-symbol]')) {
      if (el.textContent !== symbole) el.textContent = symbole;
    }
  }

  function appliquer(racine) {
    const u = unite();
    const cible = racine || document;
    for (const el of cible.querySelectorAll('[data-pda-dim-cm]')) convertirElement(el, u);
    convertirSymboles(u, cible);
    /* Le libellé du sélecteur et l'état des boutons : ils vivent dans le header, jamais
     * dans une racine réinjectée — on les met à jour depuis le document. ⛔ Le sélecteur
     * géo est rendu DEUX FOIS (header.liquid uid '-nav' + header-drawer.liquid uid
     * '-drawer') : il y a donc DEUX [data-pda-unit-label], comme il y a deux paires de
     * boutons. Un `querySelector` SINGULIER ne réécrivait que la première instance du
     * DOM — le trigger desktop restait « Centimètres » avec le dot sur « Pouces »
     * (signalé par Sam le 25/08). Même itération que les boutons, toujours. */
    const uLabel = trad(u === 'in' ? 'pda.geo.unite_in' : 'pda.geo.unite_cm');
    for (const label of document.querySelectorAll('[data-pda-unit-label]')) {
      if (label.textContent !== uLabel) label.textContent = uLabel;
    }
    for (const b of document.querySelectorAll('[data-pda-unit]')) {
      const actif = b.getAttribute('data-pda-unit') === u;
      b.classList.toggle('is-active', actif);
      b.setAttribute('aria-pressed', actif ? 'true' : 'false');
    }
    /* ⛔ NOM DISTINCT DU BOUTON, ET CE N'EST PAS UN DÉTAIL. Ce marqueur s'appelait
     * `data-pda-unit`, comme les boutons de choix — donc `closest('[data-pda-unit]')`
     * du gestionnaire ci-dessous matchait <html> À CHAQUE CLIC DE LA PAGE, et appelait
     * `preventDefault()` sur tous. Mesuré le 20/08 en piégeant `preventDefault` : plus
     * aucun `<details>` ne s'ouvrait, et les liens ne naviguaient plus. Un sélecteur
     * d'attribut posé sur la racine attrape TOUT ce qui est en dessous. */
    document.documentElement.setAttribute('data-pda-unite-active', u);
  }

  function choisir(u) {
    if (u !== 'cm' && u !== 'in') return;
    ecrire(u);
    appliquer(document);
    /* ⚠️ Aucun rechargement : le choix d'unité n'est PAS un mécanisme Shopify. Recharger
     * ferait perdre l'état du moteur de filtres (page courante, sélection, scroll) pour
     * un changement purement typographique. */
    document.dispatchEvent(new CustomEvent('pda:unit', { detail: { unite: u } }));
  }

  // ── câblage ───────────────────────────────────────────────────────────────
  document.addEventListener('click', (e) => {
    /* ⛔ `button[data-pda-unit]`, PAS `[data-pda-unit]`. La forme large a coûté cher :
     * combinée au marqueur de racine (corrigé ci-dessus), elle annulait tous les clics
     * du site. La restreindre au BOUTON rend le sélecteur incapable d'attraper un
     * ancêtre, quelle que soit la suite. Ceinture ET bretelles, délibérément. */
    const b = e.target.closest('button[data-pda-unit]');
    if (!b) return;
    e.preventDefault();
    choisir(b.getAttribute('data-pda-unit'));
  });

  /* ⛔ L'OBSERVATEUR EST LA CONDITION DU BON FONCTIONNEMENT, PAS UN RAFFINEMENT.
   * Les grilles reviennent du serveur en centimètres à chaque filtre, chaque page
   * suivante, chaque changement de tri. Sans lui, « pouces » se perdrait carte par carte.
   * ⚠️ On observe le DOCUMENT et pas une grille précise : les surfaces réinjectées ne
   * sont pas les mêmes selon la page (catalogue, /search, favoris, carrousels), et une
   * liste de racines à tenir à jour serait une liste de plus à laisser diverger. */
  const observateur = new MutationObserver((mutations) => {
    let aBesoin = false;
    for (const m of mutations) {
      for (const n of m.addedNodes) {
        if (n.nodeType !== 1) continue;
        if (n.matches?.('[data-pda-dim-cm],[data-pda-unit-symbol]') || n.querySelector?.('[data-pda-dim-cm],[data-pda-unit-symbol]')) {
          aBesoin = true; break;
        }
      }
      if (aBesoin) break;
    }
    if (aBesoin) appliquer(document);
  });

  const demarrer = () => {
    appliquer(document);
    observateur.observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', demarrer);
  else demarrer();

  window.PDA = window.PDA || {};
  window.PDA.unite = unite;
  window.PDA.choisirUnite = choisir;

  /* ⛔ LA SOURCE UNIQUE DU FACTEUR, ET LA SEULE. Le filtre de dimensions demande QUATRE
   * conversions sur le chemin d'entrée du moteur : lecture du champ texte, écriture par
   * le curseur, placeholder, et libellé du chip. Quatre `* 2.54` éparpillés, c'est quatre
   * endroits où oublier un sens, et le symptôme serait une grille qui rend des résultats
   * plausibles mais faux — sans erreur, sans log, sans rien à l'écran.
   * ⚠️ CES DEUX FONCTIONS NE SONT PAS SYMÉTRIQUES DANS L'USAGE : `versCm` s'applique à ce
   * que le VISITEUR SAISIT (donc avant d'entrer dans l'état du moteur), `versAffichage` à
   * ce qu'on lui MONTRE (donc après en être sorti). L'état, l'index et l'URL restent en
   * centimètres de bout en bout — les jetons d'URL `larg_max` portent des cm, et les
   * liens du méga-menu (« Petit format < 40 cm », GO Sam) en dépendent.
   * ⚠️ LE PRIX ET L'ANNÉE NE SONT PAS DES LONGUEURS. L'appelant passe la clé de facette :
   * seules `w` et `hg` convertissent. Sans ce garde-fou, un prix passerait en « pouces ». */
  const LONGUEURS = new Set(['w', 'hg']);
  /* ⛔ ON ARRONDIT AU CENTIMÈTRE ENTIER À LA SAISIE, ET CE N'EST PAS DE LA COQUETTERIE.
   * L'affichage en pouces garde UNE décimale : 40 cm se montre « 15,7 in ». Retaper
   * 15,7 rendait 39,878 cm — donc un filtre « ≤ 39,878 » qui EXCLUT les œuvres de 40 cm.
   * Mesuré le 20/08 : le compteur passait de 8 462 à 7 813 sans que le visiteur ait rien
   * voulu changer, juste en touchant un champ.
   * Les dimensions de l'index sont des ENTIERS en centimètres (« 43.0,33.0 ») : arrondir
   * à cette granularité rend l'aller-retour stable et ne perd aucune finesse réelle. */
  window.PDA.versCm = (v, cle) =>
    (v != null && LONGUEURS.has(cle) && unite() === 'in') ? Math.round(v * CM_PAR_POUCE) : v;
  window.PDA.versAffichage = (v, cle) =>
    (v != null && LONGUEURS.has(cle) && unite() === 'in') ? v / CM_PAR_POUCE : v;
})();
